import React, { useCallback, useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from '../../_layouts/CommonLayout';
import Filters from './Filters';
import RiskPoolOrganizations from './RiskPoolOrganizations';
import ComplianceOverview from './ComplianceOverview';
import { error, success, warning } from '_lex/_foundations/phxColors';
import dayjs from 'dayjs';
import _ from 'lodash';
import { Routes, Route, useSearchParams } from 'react-router-dom';
import RiskPoolOrganizationDetail from './RiskPoolOrganizations/Detail';
import { AppContext } from 'views/App/context';
import useComplianceOverview from './_data_sources/useComplianceOverview';
import useRiskPoolOrganizations from './_data_sources/useRiskPoolOrganizations';

export const compliancePerformanceColors = {
  compliant: success[600],
  partial: warning[400],
  nonCompliant: error[700],
};

const defaultDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

function RiskPoolDashboardMain() {
  const { currentOrganizationId } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      date: searchParams.get('date') || defaultDate,
      compliance: searchParams.get('compliance') || '',
      sortOrder: searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc',
    }),
    [searchParams]
  );

  const updateParams = useCallback(
    (updatedParams: Partial<{ search: string; date: string; compliance: string; sortOrder: string }>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updatedParams).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        return next;
      });
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setSearchParams]
  );

  const clearParams = useCallback(() => {
    setSearchParams((prev) => {
      const next: Record<string, string> = { date: defaultDate };
      if (prev.get('sortOrder') === 'desc') next.sortOrder = 'desc';
      return next;
    });
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setSearchParams]);

  const queryParams = useMemo(() => {
    return _.omitBy(
      { search: params.search, date: params.date, compliance: params.compliance, sortOrder: params.sortOrder },
      _.isEmpty
    );
  }, [params]);

  const isFiltered = !_.isEmpty(_.omit(queryParams, ['date', 'sortOrder']));

  const organizationsQuery = useRiskPoolOrganizations(currentOrganizationId, queryParams);

  const organizations = useMemo(
    () => (organizationsQuery.data?.pages ? organizationsQuery.data.pages.flatMap((page) => page.organizations) : []),
    [organizationsQuery.data]
  );

  const totalCount = useMemo(
    () => organizationsQuery.data?.pages?.[0]?.totalCount ?? 0,
    [organizationsQuery.data]
  );

  const complianceOverviewQuery = useComplianceOverview(
    currentOrganizationId,
    {
      date: params.date,
      search: params.search,
      compliance: params.compliance,
    }
  );

  const displayDate = params.date ? dayjs(params.date).format('MM/DD/YYYY') : dayjs().format('MM/DD/YYYY');

  return (
    <CommonLayout
      headerProps={{
        title: t('dashboard.riskPool.title'),
        subtitle: t('dashboard.riskPool.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0, gap: 4 }}>
        <Filters
          initialSearch={params.search}
          initialDate={params.date}
          initialCompliance={params.compliance}
          updateParams={updateParams}
          isFiltered={isFiltered}
          onClearFilters={clearParams}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0, overflow: 'auto' }}>
          <Typography variant='subtitleLg'>
            {t('dashboard.riskPool.metricsHeading', '', { date: displayDate })}
          </Typography>
          <Box>
            <ComplianceOverview
              data={complianceOverviewQuery.data}
              isLoading={complianceOverviewQuery.isLoading}
              isFetching={complianceOverviewQuery.isFetching}
              isError={complianceOverviewQuery.isError}
              error={complianceOverviewQuery.error}
              updateParams={updateParams}
            />
          </Box>
          <RiskPoolOrganizations
            organizations={organizations}
            isLoading={organizationsQuery.isLoading}
            query={organizationsQuery}
            totalCount={totalCount}
            sortOrder={params.sortOrder}
            updateParams={updateParams}
          />
        </Box>
      </Box>
    </CommonLayout>
  );
}

const RiskPoolDashboard: React.FC = () => {
  return (
    <Routes>
      <Route
        path='/'
        element={<RiskPoolDashboardMain />}
      />
      <Route
        path=':orgId'
        element={<RiskPoolOrganizationDetail />}
      />
    </Routes>
  );
};

export default RiskPoolDashboard;
